import { parseHex } from './contrast'

/**
 * Colourblind mode: a texture layered over each block's colour.
 *
 * Roughly one man in twelve cannot reliably separate red from green, and this
 * app asks you to tell a dozen colours apart in 26-pixel strips. Adding a
 * pattern gives a second, non-chromatic channel — two blocks can then differ by
 * texture even when they look identical.
 *
 * The pattern is derived from the colour's HUE, not from the type's id. That
 * matters: it means two types that look alike get different textures, which is
 * exactly the pair you need to distinguish, and it stays stable when types are
 * reordered or renamed.
 *
 * Pure CSS gradients rather than SVG data URIs — no extra bytes, no image
 * decode, and they scale with the block instead of tiling at a fixed size.
 */

/** Six textures, spaced so no two read alike at 26px tall. */
const PATTERNS: readonly string[] = [
  // 0 — none. The most common hue band stays clean, so the majority of blocks
  // look exactly as they do with the mode off.
  'none',
  // 1 — forward diagonal hatch
  'repeating-linear-gradient(45deg, PATTERN_INK 0 2px, transparent 2px 6px)',
  // 2 — back diagonal hatch
  'repeating-linear-gradient(-45deg, PATTERN_INK 0 2px, transparent 2px 6px)',
  // 3 — horizontal rules
  'repeating-linear-gradient(0deg, PATTERN_INK 0 1px, transparent 1px 5px)',
  // 4 — vertical rules
  'repeating-linear-gradient(90deg, PATTERN_INK 0 1px, transparent 1px 5px)',
  // 5 — cross hatch
  'repeating-linear-gradient(45deg, PATTERN_INK 0 1px, transparent 1px 6px), repeating-linear-gradient(-45deg, PATTERN_INK 0 1px, transparent 1px 6px)',
]

export const PATTERN_COUNT = PATTERNS.length

/** Hue in degrees, 0–360. Returns 0 for greys, which have no meaningful hue. */
export function hueOf(hex: string): number {
  const rgb = parseHex(hex)
  if (!rgb) return 0

  const r = rgb.r / 255
  const g = rgb.g / 255
  const b = rgb.b / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min
  if (delta === 0) return 0

  let hue: number
  if (max === r) hue = ((g - b) / delta) % 6
  else if (max === g) hue = (b - r) / delta + 2
  else hue = (r - g) / delta + 4

  hue *= 60
  return hue < 0 ? hue + 360 : hue
}

/** Which of the six textures a colour gets. Deterministic, hue-banded. */
export function patternIndexFor(hex: string): number {
  return Math.floor(hueOf(hex) / (360 / PATTERN_COUNT)) % PATTERN_COUNT
}

/**
 * A `background-image` value for a block of this colour, or null for no
 * pattern.
 *
 * `ink` is the colour the texture is drawn in — pass the same value the label
 * uses, so the texture is visible on both light and dark fills without a
 * second contrast calculation.
 */
export function patternFor(hex: string, ink: string): string | null {
  const template = PATTERNS[patternIndexFor(hex)]
  if (!template || template === 'none') return null

  // A quarter-strength ink: the texture has to be perceptible without
  // competing with the label sitting on top of it.
  const tint = ink === '#ffffff' ? 'rgb(255 255 255 / 28%)' : 'rgb(0 0 0 / 22%)'
  return template.replaceAll('PATTERN_INK', tint)
}
